import { useEffect, useState } from 'react'
import { useAuthStore } from '@carrent/core'
import { PageHeader } from '@carrent/ui'
import { StatusBadge } from '@carrent/ui'
import { api } from '@carrent/api'

// ── Круговой индикатор рейтинга ───────────────────────────────────────────────
function RatingCircle({ score, label, color }) {
  const r   = 44
  const circ = 2 * Math.PI * r
  const pct  = Math.max(0, Math.min(100, score)) / 100
  const dash = circ * pct
  const gap  = circ - dash

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative', width: 110, height: 110 }}>
        <svg width="110" height="110" style={{ transform: 'rotate(-90deg)' }}>
          {/* Фон */}
          <circle cx="55" cy="55" r={r} fill="none"
            stroke="var(--border)" strokeWidth="8" />
          {/* Прогресс */}
          <circle cx="55" cy="55" r={r} fill="none"
            stroke={color} strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${gap}`}
            style={{ transition: 'stroke-dasharray 0.8s ease' }}
          />
        </svg>
        {/* Число */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color, lineHeight: 1 }}>
            {Math.round(score)}
          </span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 1 }}>из 100</span>
        </div>
      </div>
      <span style={{ fontSize: '0.9rem', fontWeight: 700, color }}>{label}</span>
    </div>
  )
}

// ── Строка статистики ─────────────────────────────────────────────────────────
function StatRow({ icon, label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px var(--gap)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1.2rem', width: 24, textAlign: 'center' }}>{icon}</span>
        <div>
          <p style={{ fontSize: '0.88rem' }}>{label}</p>
          {sub && <p className="text-muted" style={{ fontSize: '0.75rem' }}>{sub}</p>}
        </div>
      </div>
      <span style={{ fontWeight: 700, fontSize: '1rem', color: color || 'var(--text)' }}>
        {value}
      </span>
    </div>
  )
}

export default function ProfilePage() {
  const { user, logout } = useAuthStore()
  const [stats, setStats]   = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user?.status !== 'approved') return
    setLoading(true)
    api.get('/users/me/stats')
      .then(r => setStats(r.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [user?.id])

  const isDriver = user?.role === 'user'

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader title="Профиль" />
      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>

        {/* Аватар + имя + статус */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap)', paddingTop: 4 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
            background: user?.role === 'admin' ? '#8b5cf6'
              : user?.role === 'manager' ? '#f59e0b' : 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.8rem', fontWeight: 800, color: '#fff',
          }}>
            {user?.full_name?.[0] || '?'}
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>{user?.full_name}</p>
            <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
              <StatusBadge status={user?.status} />
              <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                {user?.role === 'admin' ? '👑 Администратор'
                  : user?.role === 'manager' ? '🔧 Менеджер' : '🚗 Водитель'}
              </span>
            </div>
          </div>
        </div>

        {/* Контакты */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {[
            { l: 'Телефон', v: user?.phone },
            { l: 'Email',   v: user?.email || '—' },
          ].map(({ l, v }) => (
            <div key={l} style={{ display: 'flex', padding: '11px var(--gap)',
              borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted" style={{ minWidth: 90, fontSize: '0.85rem' }}>{l}</span>
              <span style={{ fontSize: '0.92rem' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Статусные алерты */}
        {user?.status === 'draft' && (
          <div className="alert alert--info">
            Чтобы начать поездку, завершите верификацию на главной странице.
          </div>
        )}
        {user?.status === 'pending' && (
          <div className="alert alert--info">Ваша заявка на проверке у администратора.</div>
        )}
        {user?.status === 'blocked' && (
          <div className="alert alert--error">
            Аккаунт заблокирован: {user.blocked_reason || 'причина не указана'}
          </div>
        )}

        {/* ── Блок рейтинга и статистики (только для approved водителей) ── */}
        {isDriver && user?.status === 'approved' && (
          <>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--gap)' }}>
                <div className="spinner" />
              </div>
            ) : stats ? (
              <>
                {/* Рейтинг */}
                <p className="section-title">Рейтинг водителя</p>
                <div className="card" style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--gap)',
                  background: `${stats.color}08`,
                  border: `1px solid ${stats.color}30`,
                }}>
                  <RatingCircle score={stats.score} label={stats.label} color={stats.color} />
                  <div style={{ flex: 1 }}>
                    {/* Штрафы */}
                    {stats.penalties?.map((p, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                        fontSize: '0.78rem', marginBottom: 4 }}>
                        <span className="text-muted">{p.reason}</span>
                        <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{p.value}</span>
                      </div>
                    ))}
                    {/* Бонусы */}
                    {stats.bonuses?.map((b, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                        fontSize: '0.78rem', marginBottom: 4 }}>
                        <span className="text-muted">{b.reason}</span>
                        <span style={{ color: 'var(--success)', fontWeight: 700 }}>+{b.value}</span>
                      </div>
                    ))}
                    {!stats.penalties?.length && !stats.bonuses?.length && (
                      <p className="text-muted" style={{ fontSize: '0.82rem' }}>
                        Начни поездки чтобы рейтинг наполнился
                      </p>
                    )}
                  </div>
                </div>

                {/* Статистика поездок */}
                <p className="section-title">Статистика</p>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <StatRow icon="🚗" label="Поездок всего"
                    value={stats.trips_total} />
                  <StatRow icon="✅" label="Завершено"
                    value={stats.trips_finished}
                    color="var(--success)" />
                  <StatRow icon="🛣" label="Пройдено"
                    value={`${(stats.total_distance || 0).toLocaleString()} км`} />
                  {stats.trips_damaged > 0 && (
                    <StatRow icon="⚠️" label="Поездки с повреждениями"
                      value={stats.trips_damaged}
                      color="var(--danger)" />
                  )}
                </div>

                {/* Финансовая сводка */}
                {stats.total_expenses > 0 && (
                  <>
                    <p className="section-title">Расходы</p>
                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      <StatRow icon="💰" label="Всего потрачено"
                        value={`${stats.total_expenses.toLocaleString()} ₽`}
                        color="var(--warning)" />
                      {stats.total_fuel > 0 && (
                        <StatRow icon="⛽" label="Заправлено топлива"
                          value={`${stats.total_fuel.toFixed(0)} л`} />
                      )}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </>
        )}

        {/* Выход */}
        <button className="btn btn--full" onClick={logout}
          style={{
            marginTop: 'var(--gap-sm)', height: 48,
            background: 'rgba(239,68,68,0.1)', color: 'var(--danger)',
            border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r)',
            cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem',
          }}>
          🚪 Выйти из аккаунта
        </button>
      </div>
    </div>
  )
}
